import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";

import logo from "../../../../assets/logo1.png";
import galaxyBg from "../../../../assets/galaxy_image.jpg";

// ===================== CLEAN TEXT ======================
const cleanText = (text = "") => text.replace(/[*"]/g, "").trim();

// ===================== PARSER ======================
const parseViolations = (text) => {
  if (!text) return [];

  const blocks = text.split(/Claim Number:/gi).slice(1);

  return blocks.map((block) => {
    const claimNumber = block.match(/^\s*(\d+)/)?.[1] || "";

    const similarPortion =
      block
        .match(
          /Portion of the Claim that is similar[:\-]?\s*([\s\S]*?)(Business Solution that caused|Reason for|Claim Number:|$)/i
        )?.[1]
        ?.trim() || "";
    const business =
      block
        .match(
          /Business Solution that caused.*?:\s*([\s\S]*?)(Reason for considering|Claim Number:|$)/i
        )?.[1]
        ?.trim() || "";

    const reason =
      block
        .match(/Reason for considering.*?:\s*([\s\S]*?)(Claim Number:|$)/i)?.[1]
        ?.trim() || "";

    return { claimNumber, similarPortion, business, reason };
  });
};

// ===================== STYLES ======================
const styles = StyleSheet.create({
  page: {
    backgroundColor: "#f9fafb",
    fontFamily: "Helvetica",
    padding: 30,
    fontSize: 11,
    color: "#1e293b",
    position: "relative",
  },

  /* ✅ Cover Page */
  coverPage: {
    position: "relative",
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: 0,
  },

  bgImage: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover", // ✅ important
  },

  logoOverlay: {
    position: "absolute",
    top: 80,
    left: 0,
    right: 0,
    marginLeft: "auto",
    marginRight: "auto",
    width: 150,
    height: 150,
  },

  coverTitle: {
    fontSize: 28,
    fontWeight: "bold",
    color: "#ffffff",
    marginTop: 100,
    marginBottom: 10,
  },

  companyInfoBlock: {
    marginTop: 270,
    width: "80%",
    textAlign: "center",
    alignSelf: "center",
  },

  companyLine: {
    fontSize: 15,
    marginBottom: 4,
    color: "#f1f5f9", // Light text for dark bg
    lineHeight: 1.1,
  },

  companyLabel: {
    fontWeight: "bold",
    color: "#ffffff",
  },

  sectionHeader: {
    fontSize: 13,
    color: "#1e3a8a",
    fontWeight: "bold",
    borderBottom: "1px solid #e5e7eb",
    paddingBottom: 4,
    marginBottom: 6,
    textTransform: "uppercase",
  },

  textRow: {
    marginBottom: 4,
    lineHeight: 0.9,
  },

  label: {
    fontWeight: "bold",
    color: "#0f172a",
  },

  /* CLAIM PAGES */
  claimHeader: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#1e40af",
    marginBottom: 10,
    textAlign: "center",
  },

  claimBox: {
    backgroundColor: "#fff7ed",
    padding: 8,
    borderRadius: 4,
    marginBottom: 10,
  },

  businessBox: {
    backgroundColor: "#eff6ff",
    padding: 8,
    borderRadius: 4,
    marginBottom: 10,
  },

  reasonBox: {
    backgroundColor: "#f0fdf4",
    padding: 8,
    borderRadius: 4,
    marginBottom: 10,
  },

  highlightText: {
    color: "#dc2626",
    fontWeight: "bold",
  },

  analysisText: {
    lineHeight: 1,
    color: "#334155",
    marginTop: 4,
    textAlign: "justify",
  },

  coverTextContainer: {
    position: "absolute",
    top: 360,
    left: 0,
    right: 0,
    textAlign: "center",
    paddingHorizontal: 20,
  },

  /* Footer pinned bottom */
  footer: {
    position: "absolute",
    bottom: 18,
    left: 0,
    right: 0,
    textAlign: "center",
    fontSize: 9,
    color: "#64748b",
    paddingTop: 4,
    borderTop: "1px solid #cbd5e1",
  },
});

// =================== MAIN DOCUMENT ===================
const InfringementReportPDF = ({ data }) => {
  const violationText = cleanText(data?.violationResult || "");
  const claims = parseViolations(violationText);

  return (
    <Document>
      {/* ✅ ✅ COVER PAGE ✅ ✅ */}
      <Page style={styles.coverPage}>
        <Image
          src={galaxyBg}
          style={styles.bgImage}
          fixed // ✅ Force to stay on page
        />

        {/* <Image src={logo} style={styles.logoOverlay} /> */}
        <View
          style={{
            position: "absolute",
            top: 240,
            width: "100%",
            alignItems: "center",
          }}
        >
          <Image src={logo} style={{ width: 200, height: 200 }} />
        </View>

        <View style={styles.coverTextContainer}>
          <Text style={styles.coverTitle}>Patent Infringement Report</Text>
        </View>

        <View style={styles.companyInfoBlock}>
          <Text style={styles.companyLine}>
            <Text style={styles.companyLabel}>Company name: </Text>
            {data.companyName || "N/A"}
          </Text>

          <Text style={styles.companyLine}>
            <Text style={styles.companyLabel}>Company email: </Text>
            {data.companyEmail || "N/A"}
          </Text>

          {/* <Text style={styles.companyLine}>
            <Text style={styles.companyLabel}>Contact number: </Text>
            {data.companyContactNumber || "N/A"}
          </Text> */}

          <Text style={styles.companyLine}>
            <Text style={styles.companyLabel}>Website: </Text>
            {data.companyUrlOrBusinessIdea || "N/A"}
          </Text>
        </View>
      </Page>

      {/* ✅ ✅ ONE CLAIM PER PAGE ✅ ✅ */}
      {/* {claims.map((c, i) => (
        <Page key={i} style={styles.page}>
          <Text style={styles.claimHeader}>Claim #{c.claimNumber}</Text>

          {c?.similarPortion ? (
            <View style={styles.claimBox}>
              <Text style={styles.highlightText}>Similar Claim Portion:</Text>
              <Text style={styles.analysisText}>{c.similarPortion}</Text>
            </View>
          ) : null}

          {c?.business ? (
            <View style={styles.businessBox}>
              <Text style={styles.label}>Business Solution Trigger:</Text>
              <Text style={styles.analysisText}>{c.business}</Text>
            </View>
          ) : null}

          {c?.reason ? (
            <View style={styles.reasonBox}>
              <Text style={styles.label}>Reason for Infringement:</Text>
              <Text style={styles.analysisText}>{c.reason}</Text>
            </View>
          ) : null}

          <Text style={styles.footer}>
            © {new Date().getFullYear()} OHanlonIP, LLC. All Rights Reserved.
          </Text>
        </Page>
      ))} */}

      {/* ✅ ✅ CLAIM LOGIC ✅ ✅ */}
      {claims.length > 0 ? (
        claims.map((c, i) => (
          <Page key={i} style={styles.page}>
            <Text style={styles.claimHeader}>Claim #{c.claimNumber}</Text>

            {c?.similarPortion ? (
              <View style={styles.claimBox}>
                <Text style={styles.highlightText}>Similar Claim Portion:</Text>
                <Text style={styles.analysisText}>{c.similarPortion}</Text>
              </View>
            ) : null}

            {c?.business ? (
              <View style={styles.businessBox}>
                <Text style={styles.label}>Business Solution Trigger:</Text>
                <Text style={styles.analysisText}>{c.business}</Text>
              </View>
            ) : null}

            {c?.reason ? (
              <View style={styles.reasonBox}>
                <Text style={styles.label}>Reason for Infringement:</Text>
                <Text style={styles.analysisText}>{c.reason}</Text>
              </View>
            ) : null}

            <Text style={styles.footer}>
              © {new Date().getFullYear()} OHanlonIP, LLC. All Rights Reserved.
            </Text>
          </Page>
        ))
      ) : (
        /* ✅ FALLBACK PAGE FOR RAW AI CONTENT */
        <Page style={styles.page}>
          <Text style={styles.sectionHeader}>Infringement Analysis</Text>
          <Text style={styles.analysisText}>{violationText}</Text>

          <Text style={styles.footer}>
            © {new Date().getFullYear()} OHanlonIP, LLC. All Rights Reserved.
          </Text>
        </Page>
      )}
    </Document>
  );
};

export default InfringementReportPDF;
